import { useState, useEffect, useRef, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { customInstance } from "@/lib/axios";
import { useAuth } from "@/contexts/auth-context";
import { useViewingOrg } from "@/contexts/viewing-org-context";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ApplicationLinkQrSection } from "@/components/application-link-qr";
import { toast } from "sonner";
import { Upload } from "lucide-react";

interface Application {
  id: number;
  internal_id: string;
  equipment_name: string;
  requested_amount: string;
  stage: string;
  invite_url?: string | null;
  primary_contact: {
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
  };
  company: {
    name: string;
  } | null;
  assigned_sales_rep: {
    first_name: string;
    last_name: string;
  } | null;
  stips_requested: boolean;
  application_signed: boolean;
  updated_at: string;
  created_at: string;
  est_close_date?: string | null;
  funded_date?: string | null;
  is_archived?: boolean;
  prequal_offer_selected?: boolean;
  organization_name?: string;
}

interface PaginatedResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: Application[];
}

type SortField = 'created_at' | 'est_close_date' | 'updated_at' | 'funded_date' | 'requested_amount' | 'internal_id' | 'equipment_name' | 'stage' | 'stips_requested' | 'application_signed' | 'primary_contact__first_name' | 'assigned_sales_rep__first_name' | 'company__name' | 'organization__name' | '';
type SortDirection = 'asc' | 'desc';

/**
 * Format a date string to display in America/New_York timezone (EST/EDT)
 * This ensures dates match the backend filter timezone
 */
function formatDateInEST(dateString: string): string {
  const date = new Date(dateString);
  // Format in America/New_York timezone to match backend filter
  return date.toLocaleDateString('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
}

// Format date-only fields (no timezone conversion)
function formatDateOnly(dateString: string | null | undefined): string {
  if (!dateString) return '-';
  // If already in YYYY-MM-DD format, parse as local date
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    const [year, month, day] = dateString.split('-');
    return `${month}/${day}/${year}`;
  }
  // Fallback for other formats
  try {
    const date = new Date(dateString + 'T00:00:00'); // Add time to avoid UTC interpretation
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  } catch (e) {
    return dateString;
  }
}

/** Parse optional non-negative int from invite equipment fields (supports 0; avoids truthy bugs). */
function parseEquipmentOptionalInt(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  const s = String(value).trim();
  if (s === "") return null;
  const n = parseInt(s.replace(/[^\d]/g, ""), 10);
  return Number.isNaN(n) ? null : n;
}

function isAcceptableSignedAuthFile(file: File): boolean {
  const name = file.name.toLowerCase();
  const type = (file.type || "").toLowerCase();
  if (type === "application/pdf" || name.endsWith(".pdf")) return true;
  if (type.startsWith("image/")) return true;
  if (/\.(png|jpg|jpeg|webp|gif)$/.test(name)) return true;
  return false;
}

/** User-facing copy for manual intake failures (maps legacy API field names too). */
function messageForManualIntakeFailure(error: unknown): string {
  const e = error as {
    message?: string;
    response?: { data?: { error?: string; organization_id?: string[] } };
  };
  const orgMsg = e?.response?.data?.organization_id?.[0];
  const apiError = e?.response?.data?.error;
  const raw =
    (typeof orgMsg === "string" ? orgMsg : "") ||
    (typeof apiError === "string" ? apiError : "") ||
    (typeof e?.message === "string" ? e.message : "");
  if (!raw.trim()) return "Could not create the manual application link. Please try again.";
  const t = raw.toLowerCase();
  if (t.includes("signed_authorization") || t.includes("has_signed_authorization")) {
    return "When that box is checked, attach a PDF or image of the signed authorization, then try again.";
  }
  return raw;
}

export function ApplicationsListPage() {
  const { user } = useAuth();
  const { viewingOrgId } = useViewingOrg();
  /** Staff + Demco program users: null = all orgs in scope. Dealer ORG_ADMIN/SALES: always their org. */
  const isProgramMultiDealer =
    user?.role === "TERRITORY_SALES_MANAGER" ||
    (user?.role === "ORG_ADMIN" && user?.organization_type === "MANUFACTURER");
  const effectiveOrgId = isProgramMultiDealer
    ? viewingOrgId
    : viewingOrgId ?? user?.organization ?? null;
  const selectOrgHint = isProgramMultiDealer
    ? "Select a dealer first"
    : "Select an organization first";
  /** Org column when viewing across multiple dealers (staff all-orgs, TSM, Demco HQ). */
  const showOrganizationColumn =
    ((user?.role === "SUPERADMIN" || user?.role === "DD_ACCOUNT_MANAGER") && viewingOrgId === null) ||
    (isProgramMultiDealer && viewingOrgId === null);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const orderingParam = searchParams.get("ordering") || "";
  const [page, setPage] = useState(parseInt(searchParams.get("page") || "1", 10));
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [sortField, setSortField] = useState<SortField>(
    orderingParam ? (orderingParam.replace('-', '') as SortField) : ''
  );
  const [sortDirection, setSortDirection] = useState<SortDirection>(
    orderingParam?.startsWith('-') ? 'desc' : 'asc'
  );

  // Check if stage rail is active (single stage param) vs multi-select (multiple stage params)
  const stageParams = searchParams.getAll("stage");
  const stage = stageParams.length === 1 ? stageParams[0] : ""; // Only treat as stage rail if exactly one value
  const isStageRailActive = stageParams.length === 1;

  // Parse stage filter: if stage rail is set, use empty array (stage rail handles filtering)
  // Otherwise, parse multiple stage values from URL (for multi-select column filter)
  const parseStageFilter = (): string[] => {
    if (isStageRailActive) {
      // Stage rail is active (single stage param), column filter should be empty
      return [];
    }
    // Get all stage values from URL (supports multiple) - this is multi-select
    return stageParams.length > 0 ? stageParams : [];
  };

  // Column filter states - use column_stage to avoid conflict with stage rail filter

  const [filters, setFilters] = useState({
    internal_id: searchParams.get("internal_id") || "",
    equipment_name: searchParams.get("equipment_name") || "",
    contact_name: searchParams.get("contact_name") || "",
    company_name: searchParams.get("company_name") || "",
    company_phone: searchParams.get("company_phone") || "",
    sales_rep_name: searchParams.get("sales_rep_name") || "",
    stage: parseStageFilter(), // Array of stage values
    stips_requested: searchParams.get("stips_requested") || "",
    application_signed: searchParams.get("application_signed") || "",
    created_at_start: searchParams.get("created_at_start") || "",
    created_at_end: searchParams.get("created_at_end") || "",
    est_close_date_start: searchParams.get("est_close_date_start") || "",
    est_close_date_end: searchParams.get("est_close_date_end") || "",
    updated_at_start: searchParams.get("updated_at_start") || "",
    updated_at_end: searchParams.get("updated_at_end") || "",
    funded_date_start: searchParams.get("funded_date_start") || "",
    funded_date_end: searchParams.get("funded_date_end") || "",
    requested_amount: searchParams.get("requested_amount") || "",
  });

  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  const [manualIntakeOpen, setManualIntakeOpen] = useState(false);
  const [manualHasSignedAuth, setManualHasSignedAuth] = useState(false);
  const [manualSignedFile, setManualSignedFile] = useState<File | null>(null);
  const manualSignedFileInputRef = useRef<HTMLInputElement>(null);
  const [jotformOpen, setJotformOpen] = useState(false);
  const [jotformReference, setJotformReference] = useState("");
  const [jotformSuccess, setJotformSuccess] = useState<{
    invite_url: string;
    token: string;
    pdf_attached: boolean;
    reused?: boolean;
  } | null>(null);
  const [startAppModalOpen, setStartAppModalOpen] = useState(false);
  const [startAppEquipment, setStartAppEquipment] = useState<{
    equipment_type_id: number | null;
    equipment_subtype_id: number | null;
    equipment_name: string;
    year: string;
    mileage: string;
    hours: string;
    price: string;
  }>({
    equipment_type_id: null,
    equipment_subtype_id: null,
    equipment_name: "",
    year: "",
    mileage: "",
    hours: "",
    price: "",
  });
  const [showAllColumns, setShowAllColumns] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [stageFilterOpen, setStageFilterOpen] = useState(false);

  // Sync page state with URL params
  useEffect(() => {
    const urlPage = parseInt(searchParams.get("page") || "1", 10);
    if (urlPage !== page) {
      setPage(urlPage);
    }
    // Sync search from URL params
    const urlSearch = searchParams.get("search") || "";
    if (urlSearch !== search) {
      setSearch(urlSearch);
    }
    // Sync filters from URL params
    const urlStageParams = searchParams.getAll("stage");
    const isStageRailActive = urlStageParams.length === 1;
    // If stage rail is set (single value), use empty array. Otherwise get all stage values for multi-select
    const stageFilterValues = isStageRailActive ? [] : urlStageParams;
    
    setFilters({
      internal_id: searchParams.get("internal_id") || "",
      equipment_name: searchParams.get("equipment_name") || "",
      contact_name: searchParams.get("contact_name") || "",
      company_name: searchParams.get("company_name") || "",
      sales_rep_name: searchParams.get("sales_rep_name") || "",
      stage: stageFilterValues, // Array of stage values
      stips_requested: searchParams.get("stips_requested") || "",
      application_signed: searchParams.get("application_signed") || "",
      created_at_start: searchParams.get("created_at_start") || "",
      created_at_end: searchParams.get("created_at_end") || "",
      updated_at_start: searchParams.get("updated_at_start") || "",
      updated_at_end: searchParams.get("updated_at_end") || "",
      requested_amount: searchParams.get("requested_amount") || "",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const createInvite = useMutation({
    mutationFn: async (equipmentPayload?: Record<string, unknown>) => {
      if (effectiveOrgId == null) {
        throw new Error(selectOrgHint);
      }
      const body: { organization_id: number; equipment?: Record<string, unknown> } = {
        organization_id: effectiveOrgId,
      };
      if (equipmentPayload && equipmentPayload.equipment_type_id) {
        const yearRaw = equipmentPayload.year;
        const yearParsed =
          yearRaw !== null && yearRaw !== undefined && String(yearRaw).trim() !== ""
            ? parseInt(String(yearRaw).replace(/\D/g, ""), 10)
            : null;
        const priceRaw = equipmentPayload.price;
        const priceParsed =
          priceRaw !== null && priceRaw !== undefined && String(priceRaw).trim() !== ""
            ? Number(String(priceRaw).replace(/[^\d.]/g, ""))
            : null;
        body.equipment = {
          equipment_type_id: equipmentPayload.equipment_type_id,
          equipment_subtype_id: equipmentPayload.equipment_subtype_id ?? null,
          make: (equipmentPayload.equipment_name as string) || null,
          model_name: null,
          year: yearParsed !== null && !Number.isNaN(yearParsed) ? yearParsed : null,
          mileage: parseEquipmentOptionalInt(equipmentPayload.mileage),
          hours: parseEquipmentOptionalInt(equipmentPayload.hours),
          price: priceParsed !== null && !Number.isNaN(priceParsed) ? priceParsed : null,
        };
      }
      const { data } = await customInstance.post("/api/intake/invites/", body);
      return data as { invite_url: string };
    },
    onSuccess: (data) => {
      setInviteUrl(data.invite_url);
      setInviteOpen(true);
      setStartAppModalOpen(false);
      setStartAppEquipment({
        equipment_type_id: null,
        equipment_subtype_id: null,
        equipment_name: "",
        year: "",
        mileage: "",
        hours: "",
        price: "",
      });
    },
    onError: (error: any) => {
      const msg = error?.response?.data?.organization_id?.[0] ?? error?.response?.data?.error ?? error?.message ?? "Failed to create invite";
      toast.error(typeof msg === "string" ? msg : "Failed to create invite");
    },
  });

  const createStaffManualIntake = useMutation({
    mutationFn: async () => {
      if (effectiveOrgId == null) {
        throw new Error(selectOrgHint);
      }
      if (manualHasSignedAuth && !manualSignedFile) {
        throw new Error("Choose a PDF or image for the signed authorization");
      }
      const fd = new FormData();
      fd.append("organization_id", String(effectiveOrgId));
      fd.append("has_signed_authorization", manualHasSignedAuth ? "true" : "false");
      if (manualHasSignedAuth && manualSignedFile) {
        fd.append("signed_authorization", manualSignedFile);
      }
      const { data } = await customInstance.post("/api/intake/staff-manual/", fd, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return data as { invite_url: string };
    },
    onSuccess: (data) => {
      setInviteUrl(data.invite_url);
      setInviteOpen(true);
      setManualIntakeOpen(false);
      setManualHasSignedAuth(false);
      setManualSignedFile(null);
      toast.success("Manual application link created.");
    },
    onError: (error: unknown) => {
      toast.error(messageForManualIntakeFailure(error));
    },
  });

  const { data: equipmentTypesData } = useQuery({
    queryKey: ["equipment-types"],
    queryFn: async () => {
      const { data } = await customInstance.get("/api/equipment-types/");
      return Array.isArray(data) ? data : (data?.results ?? []);
    },
    enabled: startAppModalOpen,
  });
  const equipmentTypes = equipmentTypesData ?? [];

  const { data: currentUser } = useQuery({
    queryKey: ["current-user"],
    queryFn: async () => {
      const { data } = await customInstance.get<{
        id: number;
        role: string;
        public_application_token?: string;
        organization_name?: string | null;
      }>("/api/auth/me/");
      return data;
    },
  });

  const { data: organizationsForFilter } = useQuery({
    queryKey: ["organizations"],
    queryFn: async () => {
      const { data } = await customInstance.get<
        { id: number; name: string; status: string }[] | { results: { id: number; name: string; status: string }[] }
      >("/api/organizations/");
      return Array.isArray(data) ? data : (data?.results ?? []);
    },
    enabled: showOrganizationColumn,
  });

  const canSeeOrgLink =
    currentUser?.role === "SUPERADMIN" ||
    currentUser?.role === "DD_ACCOUNT_MANAGER" ||
    currentUser?.role === "ORG_ADMIN" ||
    currentUser?.role === "TERRITORY_SALES_MANAGER";
  const canStaffManualIntake =
    currentUser?.role === "SUPERADMIN" ||
    currentUser?.role === "DD_ACCOUNT_MANAGER" ||
    currentUser?.role === "ORG_ADMIN" ||
    currentUser?.role === "TERRITORY_SALES_MANAGER" ||
    currentUser?.role === "SALES";
  const canImportJotform =
    currentUser?.role === "SUPERADMIN" || currentUser?.role === "DD_ACCOUNT_MANAGER";

  const importJotform = useMutation({
    mutationFn: async () => {
      if (effectiveOrgId == null) {
        throw new Error(selectOrgHint);
      }
      const reference = jotformReference.trim();
      if (!reference) {
        throw new Error("Paste the Jotform Submission ID or Ref from HubSpot.");
      }
      const { data } = await customInstance.post("/api/intake/jotform-import/", {
        organization_id: effectiveOrgId,
        reference,
      });
      return data as {
        invite_url: string;
        token: string;
        pdf_attached: boolean;
        reused?: boolean;
        pdf_error?: string | null;
      };
    },
    onSuccess: (data) => {
      setJotformSuccess({
        invite_url: data.invite_url,
        token: data.token,
        pdf_attached: data.pdf_attached,
        reused: data.reused,
      });
      setInviteUrl(data.invite_url);
      if (data.reused) {
        toast.success("This Jotform submission was already imported.");
      } else if (!data.pdf_attached) {
        toast.success("Application imported. Signed PDF was not attached — upload later if needed.");
      } else {
        toast.success("Jotform application imported.");
      }
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { error?: string } }; message?: string };
      toast.error(err?.response?.data?.error || err?.message || "Jotform import failed.");
    },
  });
  const { data: currentOrg } = useQuery({
    queryKey: ["organization", effectiveOrgId],
    queryFn: async () => {
      const { data } = await customInstance.get<{
        id: number;
        application_link_url?: string;
        name: string;
        slug?: string | null;
      }>(`/api/organizations/${effectiveOrgId}/`);
      return data;
    },
    enabled: !!effectiveOrgId && !!canSeeOrgLink,
  });

  // Restrict to org's allowed equipment types when set (empty = all types)
  const allowedEquipmentTypes = useMemo(() => {
    const allowedIds = (currentOrg as { allowed_equipment_types?: number[] })?.allowed_equipment_types;
    if (Array.isArray(allowedIds) && allowedIds.length > 0) {
      return equipmentTypes.filter((et: { id: number }) => allowedIds.includes(et.id));
    }
    return equipmentTypes;
  }, [equipmentTypes, (currentOrg as { allowed_equipment_types?: number[] })?.allowed_equipment_types]);

  const handleSort = (field: SortField) => {
    setPage(1); // Reset to first page when sorting changes
    if (sortField === field) {
      // Toggle direction if clicking same field
      const newDirection = sortDirection === 'asc' ? 'desc' : 'asc';
      setSortDirection(newDirection);
      const ordering = newDirection === 'desc' ? `-${field}` : field;
      const newParams = { ...Object.fromEntries(searchParams), ordering };
      delete newParams.page; // Remove page param to reset to page 1
      setSearchParams(newParams);
    } else {
      // New field, default to ascending
      setSortField(field);
      setSortDirection('asc');
      const newParams = { ...Object.fromEntries(searchParams), ordering: field };
      delete newParams.page; // Remove page param to reset to page 1
      setSearchParams(newParams);
    }
  };

  // Debounce refs for filter inputs
  const debounceTimers = useRef<Record<string, NodeJS.Timeout>>({});

  const handleFilterOrganizationChange = (value: string) => {
    setPage(1);
    const newParams = new URLSearchParams(searchParams);
    newParams.delete("page");
    if (value) {
      newParams.set("filter_organization_id", value);
    } else {
      newParams.delete("filter_organization_id");
    }
    setSearchParams(newParams);
  };

  const handleFilterChange = (field: string, value: string | string[]) => {
    // Update local state immediately for responsive UI
    setFilters(prev => ({ ...prev, [field]: value }));
    
    // Clear existing timer for this field
    if (debounceTimers.current[field]) {
      clearTimeout(debounceTimers.current[field]);
    }
    
    // Stage filter is handled separately in handleStageFilterToggle with debounce
    if (field === 'stage') {
      return; // Don't handle here, let handleStageFilterToggle do it
    }
    
    // Set new timer to update URL after 500ms of no typing (for other filters)
    debounceTimers.current[field] = setTimeout(() => {
      setPage(1);
      const newParams = new URLSearchParams(searchParams);
      newParams.delete("page");
      
      if (value) {
        newParams.set(field, value as string);
      } else {
        newParams.delete(field);
      }
      
      setSearchParams(newParams);
    }, 500);
  };

  const handleStageFilterToggle = (stageValue: string) => {
    const currentStages = filters.stage as string[];
    const newStages = currentStages.includes(stageValue)
      ? currentStages.filter(s => s !== stageValue)
      : [...currentStages, stageValue];
    
    // Update local state immediately for responsive UI
    setFilters(prev => ({ ...prev, stage: newStages }));
  };

  const handleApplyStageFilter = () => {
    setPage(1);
    const newParams = new URLSearchParams(searchParams);
    newParams.delete("page");
    newParams.delete("stage");
    const currentStages = filters.stage as string[];
    if (currentStages.length > 0) {
      currentStages.forEach(stageVal => {
        newParams.append("stage", stageVal);
      });
    }
    setSearchParams(newParams);
    setStageFilterOpen(false); // Close dropdown after applying
  };

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      Object.values(debounceTimers.current).forEach(timer => clearTimeout(timer));
    };
  }, []);

  // Close stage filter dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (stageFilterOpen && !target.closest('.stage-filter-dropdown')) {
        setStageFilterOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [stageFilterOpen]);

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return (
        <span className="text-gray-400 text-xs" title="Click to sort">
          ↕
        </span>
      );
    }
    return sortDirection === 'asc' ? (
      <span className="text-blue-600 text-xs" title="Sorted ascending">↑</span>
    ) : (
      <span className="text-blue-600 text-xs" title="Sorted descending">↓</span>
    );
  };

  // Read filter values from URL params directly (not from state) to prevent query refetch on every keystroke
  // Memoize to prevent object recreation on every render
  const urlFilters = useMemo(() => {
    const urlStageParams = searchParams.getAll("stage");
    const isStageRailActive = urlStageParams.length === 1;
    
    return {
      internal_id: searchParams.get("internal_id") || "",
      equipment_name: searchParams.get("equipment_name") || "",
      contact_name: searchParams.get("contact_name") || "",
      company_name: searchParams.get("company_name") || "",
      sales_rep_name: searchParams.get("sales_rep_name") || "",
      stage: isStageRailActive ? [] : urlStageParams, // Array of stage values for multi-select
      stips_requested: searchParams.get("stips_requested") || "",
      application_signed: searchParams.get("application_signed") || "",
      created_at_start: searchParams.get("created_at_start") || "",
      created_at_end: searchParams.get("created_at_end") || "",
      est_close_date_start: searchParams.get("est_close_date_start") || "",
      est_close_date_end: searchParams.get("est_close_date_end") || "",
      updated_at_start: searchParams.get("updated_at_start") || "",
      updated_at_end: searchParams.get("updated_at_end") || "",
      funded_date_start: searchParams.get("funded_date_start") || "",
      funded_date_end: searchParams.get("funded_date_end") || "",
      requested_amount: searchParams.get("requested_amount") || "",
      is_archived: searchParams.get("is_archived") === "true",
      filter_organization_id: searchParams.get("filter_organization_id") || "",
    };
  }, [searchParams]);

  // Get stats for sales rep applications
  const { data: salesRepStats } = useQuery({
    queryKey: ["sales-rep-stats", currentUser?.public_application_token],
    queryFn: async () => {
      if (!currentUser?.public_application_token) return null;
      const params = new URLSearchParams({
        application_source_token: currentUser.public_application_token,
      });
      const { data } = await customInstance.get<PaginatedResponse>(`/api/applications/?${params}`);
      return data.count;
    },
    enabled: !!currentUser?.public_application_token && currentUser?.role === 'SALES',
  });

  const { data, isLoading } = useQuery({
    queryKey: ["applications", page, stage, search, sortField, sortDirection, urlFilters, effectiveOrgId],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set("page", page.toString());
      if (effectiveOrgId != null) {
        params.set("organization_id", String(effectiveOrgId));
      }
      // Stage rail filter (single value) - only add if stage rail is active
      if (isStageRailActive && stage) {
        params.set("stage", stage);
      }
      if (search) {
        params.set("search", search);
      }
      if (sortField) {
        params.set("ordering", sortDirection === 'desc' ? `-${sortField}` : sortField);
      }
      if (urlFilters.internal_id) {
        params.set("internal_id", urlFilters.internal_id);
      }
      if (urlFilters.equipment_name) {
        params.set("equipment_name", urlFilters.equipment_name);
      }
      if (urlFilters.contact_name) {
        params.set("contact_name", urlFilters.contact_name);
      }
      if (urlFilters.company_name) {
        params.set("company_name", urlFilters.company_name);
      }
      if (urlFilters.company_phone) {
        params.set("company_phone", urlFilters.company_phone);
      }
      if (urlFilters.sales_rep_name) {
        params.set("sales_rep_name", urlFilters.sales_rep_name);
      }
      // Add multiple stage values if stage filter is an array (multi-select column filter)
      if (Array.isArray(urlFilters.stage) && urlFilters.stage.length > 0) {
        urlFilters.stage.forEach(stageValue => {
          params.append("stage", stageValue);
        });
      }
      if (urlFilters.stips_requested) {
        params.set("stips_requested", urlFilters.stips_requested);
      }
      if (urlFilters.application_signed) {
        params.set("application_signed", urlFilters.application_signed);
      }
      if (urlFilters.created_at_start) {
        params.set("created_at_start", urlFilters.created_at_start);
      }
      if (urlFilters.created_at_end) {
        params.set("created_at_end", urlFilters.created_at_end);
      }
      if (urlFilters.est_close_date_start) {
        params.set("est_close_date_start", urlFilters.est_close_date_start);
      }
      if (urlFilters.est_close_date_end) {
        params.set("est_close_date_end", urlFilters.est_close_date_end);
      }
      if (urlFilters.updated_at_start) {
        params.set("updated_at_start", urlFilters.updated_at_start);
      }
      if (urlFilters.updated_at_end) {
        params.set("updated_at_end", urlFilters.updated_at_end);
      }
      if (urlFilters.funded_date_start) {
        params.set("funded_date_start", urlFilters.funded_date_start);
      }
      if (urlFilters.funded_date_end) {
        params.set("funded_date_end", urlFilters.funded_date_end);
      }
      if (urlFilters.requested_amount) {
        params.set("requested_amount", urlFilters.requested_amount);
      }
      if (urlFilters.is_archived) {
        params.set("is_archived", "true");
      }
      if (urlFilters.filter_organization_id) {
        params.set("filter_organization_id", urlFilters.filter_organization_id);
      }

      const { data } = await customInstance.get<PaginatedResponse>(`/api/applications/?${params.toString()}`);
      return data;
    },
    staleTime: 0, // Always consider data stale to prevent caching issues
  });

  const stageColors: Record<string, string> = {
    CREATED: "bg-blue-500",
    IN_PROGRESS: "bg-orange-500",
    RECEIVED: "bg-gray-500",
    IN_REVIEW: "bg-yellow-500",
    SUBMITTED: "bg-blue-500",
    APPROVED: "bg-green-500",
    CONTRACTS_REQUESTED: "bg-amber-500",
    CONTRACTS_OUT: "bg-purple-500",
    CONTRACTS_IN: "bg-indigo-500",
    FUNDED: "bg-emerald-500",
    DECLINED: "bg-red-500",
    DEAL_LOST: "bg-red-500",
  };

  const stageLabels: Record<string, string> = {
    CREATED: "Created",
    IN_PROGRESS: "In Progress",
    RECEIVED: "Received",
    IN_REVIEW: "In Review",
    SUBMITTED: "Submitted",
    APPROVED: "Approved",
    CONTRACTS_REQUESTED: "Contracts Requested",
    CONTRACTS_OUT: "Contracts Out",
    CONTRACTS_IN: "Contracts In",
    FUNDED: "Funded",
    DECLINED: "Declined",
    DEAL_LOST: "Deal Lost",
  };

  const isSales = currentUser?.role === 'SALES';
  const applicationLink = currentUser?.public_application_token 
    ? `${window.location.origin}/apply/rep/${currentUser.public_application_token}`
    : null;

  const copyLink = () => {
    if (applicationLink) {
      navigator.clipboard.writeText(applicationLink);
      toast.success("Application link copied to clipboard!");
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Applications</h1>
        <p className="mt-2 text-sm text-gray-600">Manage all finance applications</p>
      </div>

      {/* Pending org: can log in but cannot create applications or use application links */}
      {user && (user.role === "ORG_ADMIN" || user.role === "SALES") && user.organization_is_active === false && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="pt-6">
            <p className="text-sm text-amber-800">
              Your organization is pending approval. You can sign in and view the portal, but you cannot create applications or use your application link until an administrator activates your organization.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Organization Application Link (admins/FIs – one link per org) */}
      {canSeeOrgLink && currentOrg?.application_link_url && (
        <Card>
          <CardHeader>
            <CardTitle>Organization Application Link</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-sm text-gray-600 mb-2">
                Share this link with applicants for {currentOrg.name}. Each visit creates a new application under this organization.
              </p>
              <div className="flex gap-2 items-center">
                <Input
                  readOnly
                  value={currentOrg.application_link_url}
                  className="flex-1 font-mono text-sm"
                />
                <Button
                  variant="outline"
                  onClick={() => {
                    if (currentOrg.application_link_url) {
                      navigator.clipboard.writeText(currentOrg.application_link_url);
                      toast.success("Organization link copied!");
                    }
                  }}
                >
                  Copy Link
                </Button>
              </div>
            </div>
            <ApplicationLinkQrSection
              url={currentOrg.application_link_url}
              downloadBaseName={`org-apply-qr-${currentOrg.slug || currentOrg.id}`}
              headline={`Apply for financing — ${currentOrg.name}`}
              subline="Each scan starts a new application for this organization."
            />
          </CardContent>
        </Card>
      )}

      {/* Sales Rep Application Link Card */}
      {isSales && applicationLink && (
        <Card className={user?.organization_is_active === false ? "opacity-75" : undefined}>
          <CardHeader>
            <CardTitle>Your Application Link</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {user?.organization_is_active === false && (
              <p className="text-sm text-amber-700 font-medium">This link will be active when your organization is activated.</p>
            )}
            <div>
              <p className="text-sm text-gray-600 mb-2">
                Share this link with your customers to have them fill out an application. 
                Applications submitted via this link will be automatically assigned to you.
              </p>
              <div className="flex gap-2 items-center">
                <Input
                  readOnly
                  value={applicationLink}
                  className="flex-1 font-mono text-sm"
                />
                <Button onClick={copyLink} variant="outline">
                  Copy Link
                </Button>
              </div>
            </div>
            <ApplicationLinkQrSection
              url={applicationLink}
              downloadBaseName={`rep-apply-qr-${currentUser?.id ?? "rep"}`}
              headline={
                currentUser?.organization_name
                  ? `Apply with ${currentUser.organization_name}`
                  : "Finance application"
              }
              subline="Applications from this link are assigned to you."
            />
            {salesRepStats !== undefined && (
              <div className="pt-2 border-t">
                <p className="text-sm text-gray-600">
                  <span className="font-semibold text-gray-900">{salesRepStats || 0}</span> application{salesRepStats !== 1 ? 's' : ''} submitted via your link
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-start sm:items-center justify-between">
        <div className="flex gap-3 items-center flex-1 w-full sm:w-auto min-w-0">
          <Input
            placeholder="Search applications..."
            value={search}
            onChange={(e) => {
              const value = e.target.value;
              setSearch(value);
              // Debounce search updates
              if (debounceTimers.current['search']) {
                clearTimeout(debounceTimers.current['search']);
              }
              debounceTimers.current['search'] = setTimeout(() => {
                const newParams = { ...Object.fromEntries(searchParams) };
                if (value) {
                  newParams.search = value;
                } else {
                  delete newParams.search;
                }
                delete newParams.page; // Reset to page 1
                setSearchParams(newParams);
              }, 500);
            }}
            className="max-w-md w-full sm:w-auto"
          />
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={urlFilters.is_archived}
              onChange={(e) => {
                const newParams = { ...Object.fromEntries(searchParams) };
                if (e.target.checked) {
                  newParams.is_archived = "true";
                } else {
                  delete newParams.is_archived;
                }
                delete newParams.page; // Reset to page 1
                setSearchParams(newParams);
              }}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span>Show Archived</span>
          </label>
        </div>
        <div className="flex gap-2 w-full sm:w-auto items-center flex-wrap">
          <Button
            onClick={() => setStartAppModalOpen(true)}
            disabled={effectiveOrgId == null || createInvite.isPending}
            className="bg-blue-600 hover:bg-blue-700 text-white w-full sm:w-auto disabled:opacity-60 disabled:cursor-not-allowed"
            title={effectiveOrgId == null ? selectOrgHint : undefined}
          >
            {createInvite.isPending ? "Creating…" : "Start Application"}
          </Button>
          {canStaffManualIntake && (
            <Button
              type="button"
              variant="outline"
              onClick={() => setManualIntakeOpen(true)}
              disabled={effectiveOrgId == null || createStaffManualIntake.isPending}
              className="w-full sm:w-auto"
              title={effectiveOrgId == null ? selectOrgHint : undefined}
            >
              Manual intake
            </Button>
          )}
          {canImportJotform && (
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setJotformSuccess(null);
                setJotformReference("");
                setJotformOpen(true);
              }}
              disabled={effectiveOrgId == null || importJotform.isPending}
              className="w-full sm:w-auto"
              title={effectiveOrgId == null ? selectOrgHint : undefined}
            >
              Import Jotform
            </Button>
          )}
          {effectiveOrgId == null && (
            <span className="text-xs text-amber-600">{selectOrgHint}</span>
          )}
        </div>
      </div>

      {isLoading ? (
        <div>Loading...</div>
      ) : (
        <>
          {/* Mobile Card View */}
          <div className="lg:hidden space-y-3">
            {data?.results.map((app) => {
              const isExpanded = expandedRows.has(app.id);
              return (
                <Card 
                  key={app.id}
                  className="cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => {
                    if (stage) {
                      localStorage.setItem("selectedStage", stage);
                    }
                    // Clear search when navigating to application detail
                    setSearch(""); // Clear search state
                    const newParams = new URLSearchParams(searchParams);
                    newParams.delete("search");
                    navigate(`/apps/${app.id}?${newParams.toString()}`);
                  }}
                >
                  <CardContent className="p-4">
                    <div className="space-y-3">
                      {/* Primary Info */}
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-semibold text-gray-900">{app.internal_id}</span>
                            <Badge className={`${stageColors[app.stage] || "bg-gray-500"} text-xs px-2 py-0.5`}>
                              {stageLabels[app.stage] || app.stage}
                            </Badge>
                          </div>
                          <p className="text-sm font-medium text-gray-900 truncate">{app.equipment_name}</p>
                          <p className="text-sm text-gray-600">${parseFloat(app.requested_amount || app.equipment_price || '0').toLocaleString()}</p>
                        </div>
                      </div>

                      {/* Critical Info */}
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div>
                          <span className="text-gray-500">Created:</span>
                          <span className="ml-1 text-gray-900">{formatDateInEST(app.created_at)}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Updated:</span>
                          <span className="ml-1 text-gray-900">{formatDateInEST(app.updated_at)}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Contact:</span>
                          <span className="ml-1 text-gray-900">
                            {app.primary_contact ? `${app.primary_contact?.first_name || ""} ${app.primary_contact?.last_name || ""}`.trim() || "N/A" : "N/A"}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-500">Company:</span>
                          <span className="ml-1 text-gray-900">{app.company?.name || "-"}</span>
                        </div>
                        {showOrganizationColumn && (
                          <div className="col-span-2">
                            <span className="text-gray-500">Organization:</span>
                            <span className="ml-1 text-gray-900">{app.organization_name || "—"}</span>
                          </div>
                        )}
                      </div>

                      {/* Expandable Additional Info */}
                      {isExpanded && (
                        <div className="pt-2 border-t space-y-2 text-xs">
                          {stage === "RECEIVED" && (
                            <>
                              <div>
                                <span className="text-gray-500">Signed:</span>
                                <span className="ml-1">
                                  {app.application_signed ? (
                                    <Badge className="bg-green-500 text-xs px-1.5 py-0.5">Signed</Badge>
                                  ) : (
                                    <Badge variant="secondary" className="text-xs px-1.5 py-0.5">Not Signed</Badge>
                                  )}
                                </span>
                              </div>
                              <div>
                                <span className="text-gray-500">Offer:</span>
                                <span className="ml-1">
                                  {app.prequal_offer_selected ? (
                                    <Badge className="bg-green-600 text-xs px-1.5 py-0.5">Selected</Badge>
                                  ) : (
                                    <Badge variant="secondary" className="text-xs px-1.5 py-0.5">Not selected</Badge>
                                  )}
                                </span>
                              </div>
                            </>
                          )}
                          {app.assigned_sales_rep && (
                            <div>
                              <span className="text-gray-500">Sales Rep:</span>
                              <span className="ml-1 text-gray-900">
                                {`${app.assigned_sales_rep?.first_name || ""} ${app.assigned_sales_rep?.last_name || ""}`.trim()}
                              </span>
                            </div>
                          )}
                          {app.stips_requested && (
                            <div>
                              <span className="text-gray-500">Stips:</span>
                              <Badge variant="secondary" className="ml-1 text-xs px-1.5 py-0.5">Open</Badge>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Expand/Collapse Button */}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-full text-xs"
                        onClick={(e) => {
                          e.stopPropagation();
                          setExpandedRows(prev => {
                            const next = new Set(prev);
                            if (next.has(app.id)) {
                              next.delete(app.id);
                            } else {
                              next.add(app.id);
                            }
                            return next;
                          });
                        }}
                      >
                        {isExpanded ? "Show Less" : "Show More"}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Desktop/Tablet Table View */}
          <div className="hidden lg:block bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1000px]">
              <thead className="bg-blue-50 border-b border-gray-200">
                <tr>
                  {/* Application Signed - Only show in RECEIVED stage */}
                  {stage === "RECEIVED" && (
                    <th 
                      className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-blue-100 select-none"
                      onClick={() => handleSort('application_signed')}
                    >
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-1 text-[10px]">
                          Application Signed
                          {getSortIcon('application_signed')}
                        </div>
                        <select
                          value={filters.application_signed}
                          onChange={(e) => handleFilterChange('application_signed', e.target.value)}
                          className="h-6 text-[10px] px-1 border border-gray-300 rounded"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <option value="">All</option>
                          <option value="true">Signed</option>
                          <option value="false">Not Signed</option>
                        </select>
                      </div>
                    </th>
                  )}
                  {/* Offer selected - Only show in RECEIVED stage */}
                  {stage === "RECEIVED" && (
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Offer
                    </th>
                  )}
                  {/* Create Date */}
                  <th 
                    className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-blue-100 select-none"
                    onClick={() => handleSort('created_at')}
                  >
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-1 text-[10px]">
                        Create Date
                        {getSortIcon('created_at')}
                      </div>
                      <div className="flex flex-col gap-0.5">
                        <input
                          type="date"
                          placeholder="From"
                          value={filters.created_at_start || ''}
                          onChange={(e) => handleFilterChange('created_at_start', e.target.value)}
                          className="h-5 text-[9px] px-1 border border-gray-300 rounded"
                          onClick={(e) => e.stopPropagation()}
                        />
                        <input
                          type="date"
                          placeholder="To"
                          value={filters.created_at_end || ''}
                          onChange={(e) => handleFilterChange('created_at_end', e.target.value)}
                          className="h-5 text-[9px] px-1 border border-gray-300 rounded"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                    </div>
                  </th>
                  {/* Est. Close Date */}
                  <th 
                    className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-blue-100 select-none"
                    onClick={() => handleSort('est_close_date')}
                  >
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-1 text-[10px]">
                        Est. Close Date
                        {getSortIcon('est_close_date')}
                      </div>
                      <div className="flex flex-col gap-0.5">
                        <input
                          type="date"
                          placeholder="From"
                          value={filters.est_close_date_start || ''}
                          onChange={(e) => handleFilterChange('est_close_date_start', e.target.value)}
                          className="h-5 text-[9px] px-1 border border-gray-300 rounded"
                          onClick={(e) => e.stopPropagation()}
                        />
                        <input
                          type="date"
                          placeholder="To"
                          value={filters.est_close_date_end || ''}
                          onChange={(e) => handleFilterChange('est_close_date_end', e.target.value)}
                          className="h-5 text-[9px] px-1 border border-gray-300 rounded"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                    </div>
                  </th>
                  {/* Updated */}
                  <th 
                    className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-blue-100 select-none"
                    onClick={() => handleSort('updated_at')}
                  >
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-1 text-[10px]">
                        Updated
                        {getSortIcon('updated_at')}
                      </div>
                      <div className="flex flex-col gap-0.5">
                        <input
                          type="date"
                          placeholder="From"
                          value={filters.updated_at_start || ''}
                          onChange={(e) => handleFilterChange('updated_at_start', e.target.value)}
                          className="h-5 text-[9px] px-1 border border-gray-300 rounded"
                          onClick={(e) => e.stopPropagation()}
                        />
                        <input
                          type="date"
                          placeholder="To"
                          value={filters.updated_at_end || ''}
                          onChange={(e) => handleFilterChange('updated_at_end', e.target.value)}
                          className="h-5 text-[9px] px-1 border border-gray-300 rounded"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                    </div>
                  </th>
                  {showOrganizationColumn && (
                    <th
                      className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-blue-100 select-none"
                      onClick={() => handleSort("organization__name")}
                    >
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-1 text-[10px]">
                          Organization
                          {getSortIcon("organization__name")}
                        </div>
                        <select
                          value={searchParams.get("filter_organization_id") || ""}
                          onChange={(e) => handleFilterOrganizationChange(e.target.value)}
                          className="h-6 text-[10px] px-1 border border-gray-300 rounded max-w-[11rem]"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <option value="">All organizations</option>
                          {(organizationsForFilter ?? [])
                            .filter((org) => org.status === "ACTIVE")
                            .map((org) => (
                              <option key={org.id} value={org.id}>
                                {org.name}
                              </option>
                            ))}
                        </select>
                      </div>
                    </th>
                  )}
                  {/* Funded Date - Only show in FUNDED stage */}
                  {stage === "FUNDED" && (
                    <th 
                      className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-blue-100 select-none"
                      onClick={() => handleSort('funded_date')}
                    >
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-1 text-[10px]">
                          Funded Date
                          {getSortIcon('funded_date')}
                        </div>
                        <div className="flex flex-col gap-0.5">
                          <input
                            type="date"
                            placeholder="From"
                            value={filters.funded_date_start || ''}
                            onChange={(e) => handleFilterChange('funded_date_start', e.target.value)}
                            className="h-5 text-[9px] px-1 border border-gray-300 rounded"
                            onClick={(e) => e.stopPropagation()}
                          />
                          <input
                            type="date"
                            placeholder="To"
                            value={filters.funded_date_end || ''}
                            onChange={(e) => handleFilterChange('funded_date_end', e.target.value)}
                            className="h-5 text-[9px] px-1 border border-gray-300 rounded"
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                      </div>
                    </th>
                  )}
                  {/* ID */}
                  <th 
                    className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-blue-100 select-none"
                    onClick={() => handleSort('internal_id')}
                  >
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-1 text-[10px]">
                        ID
                        {getSortIcon('internal_id')}
                      </div>
                      <Input
                        type="text"
                        placeholder="..."
                        value={filters.internal_id}
                        onChange={(e) => handleFilterChange('internal_id', e.target.value)}
                        className="h-6 text-[10px] px-1"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                  </th>
                  {/* Equipment */}
                  <th 
                    className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-blue-100 select-none"
                    onClick={() => handleSort('equipment_name')}
                  >
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-1 text-[10px]">
                        Equipment
                        {getSortIcon('equipment_name')}
                      </div>
                      <Input
                        type="text"
                        placeholder="..."
                        value={filters.equipment_name}
                        onChange={(e) => handleFilterChange('equipment_name', e.target.value)}
                        className="h-6 text-[10px] px-1"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                  </th>
                  {/* Amount */}
                  <th 
                    className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-blue-100 select-none"
                    onClick={() => handleSort('requested_amount')}
                  >
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-1 text-[10px]">
                        Amount
                        {getSortIcon('requested_amount')}
                      </div>
                      <Input
                        type="text"
                        placeholder="..."
                        value={filters.requested_amount}
                        onChange={(e) => handleFilterChange('requested_amount', e.target.value)}
                        className="h-6 text-[10px] px-1"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                  </th>
                  {/* Stage */}
                  <th 
                    className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-blue-100 select-none"
                    onClick={() => handleSort('stage')}
                  >
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-1 text-[10px]">
                        Stage
                        {getSortIcon('stage')}
                      </div>
                      {!isStageRailActive ? (
                        // Multi-select filter (only on All Applications page)
                        <div className="relative stage-filter-dropdown" onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setStageFilterOpen(!stageFilterOpen);
                            }}
                            className="h-6 text-[10px] px-1 border border-gray-300 rounded w-full text-left bg-white hover:bg-gray-50 flex items-center justify-between"
                          >
                            <span className="truncate">
                              {(filters.stage as string[]).length > 0 
                                ? `${(filters.stage as string[]).length} selected`
                                : "All stages"}
                            </span>
                            <span className="ml-1">▼</span>
                          </button>
                          {stageFilterOpen && (
                            <div className="absolute z-50 mt-1 w-48 bg-white border border-gray-300 rounded shadow-lg max-h-64 overflow-y-auto stage-filter-dropdown">
                              <div className="p-2 space-y-1">
                                {Object.entries(stageLabels).map(([value, label]) => {
                                  const isChecked = (filters.stage as string[]).includes(value);
                                  return (
                                    <label
                                      key={value}
                                      className="flex items-center space-x-2 text-xs cursor-pointer hover:bg-gray-50 p-1 rounded"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={isChecked}
                                        onChange={() => handleStageFilterToggle(value)}
                                        className="rounded border-gray-300"
                                        onClick={(e) => e.stopPropagation()}
                                      />
                                      <span className="flex-1">{label}</span>
                                    </label>
                                  );
                                })}
                                <div className="border-t pt-2 mt-2">
                                  <Button
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleApplyStageFilter();
                                    }}
                                    className="w-full"
                                  >
                                    Apply
                                  </Button>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        // Show current stage as read-only (when stage rail is active)
                        <div className="h-6 text-[10px] px-1 text-gray-600 flex items-center">
                          {stageLabels[stageParams[0]] || stageParams[0]}
                        </div>
                      )}
                    </div>
                  </th>
                  {/* Contact */}
                  <th 
                    className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-blue-100 select-none"
                    onClick={() => handleSort('primary_contact__first_name')}
                  >
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-1 text-[10px]">
                        Contact
                        {getSortIcon('primary_contact__first_name')}
                      </div>
                      <Input
                        type="text"
                        placeholder="..."
                        value={filters.contact_name}
                        onChange={(e) => handleFilterChange('contact_name', e.target.value)}
                        className="h-6 text-[10px] px-1"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                  </th>
                  {/* Company */}
                  <th 
                    className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-blue-100 select-none"
                    onClick={() => handleSort('company__name')}
                  >
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-1 text-[10px]">
                        Company
                        {getSortIcon('company__name')}
                      </div>
                      <Input
                        type="text"
                        placeholder="..."
                        value={filters.company_name}
                        onChange={(e) => handleFilterChange('company_name', e.target.value)}
                        className="h-6 text-[10px] px-1"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                  </th>
                  {/* Sales Rep - Hidden on tablet unless showAllColumns */}
                  <th 
                    className={`px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-blue-100 select-none ${!showAllColumns ? 'xl:table-cell hidden' : ''}`}
                    onClick={() => handleSort('assigned_sales_rep__first_name')}
                  >
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-1 text-[10px]">
                        Sales Rep
                        {getSortIcon('assigned_sales_rep__first_name')}
                      </div>
                      <Input
                        type="text"
                        placeholder="..."
                        value={filters.sales_rep_name}
                        onChange={(e) => handleFilterChange('sales_rep_name', e.target.value)}
                        className="h-6 text-[10px] px-1"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                  </th>
                  {/* Stips - Hidden on tablet unless showAllColumns */}
                  <th 
                    className={`px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-blue-100 select-none ${!showAllColumns ? 'xl:table-cell hidden' : ''}`}
                    onClick={() => handleSort('stips_requested')}
                  >
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-1 text-[10px]">
                        Stips
                        {getSortIcon('stips_requested')}
                      </div>
                      <select
                        value={filters.stips_requested}
                        onChange={(e) => handleFilterChange('stips_requested', e.target.value)}
                        className="h-6 text-[10px] px-1 border border-gray-300 rounded"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <option value="">All</option>
                        <option value="true">Has Stips</option>
                        <option value="false">No Stips</option>
                      </select>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {data?.results.map((app) => (
                  <tr
                    key={app.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => {
                      // Store current stage in localStorage before navigating
                      if (stage) {
                        localStorage.setItem("selectedStage", stage);
                      }
                      // Clear search when navigating to application detail
                      setSearch(""); // Clear search state
                      const newParams = new URLSearchParams(searchParams);
                      newParams.delete("search");
                      navigate(`/apps/${app.id}?${newParams.toString()}`);
                    }}
                  >
                    {/* Application Signed - Only show in RECEIVED stage */}
                    {stage === "RECEIVED" && (
                      <td className="px-3 py-2 whitespace-nowrap">
                        {app.application_signed ? (
                          <Badge className="bg-green-500 text-[10px] px-1.5 py-0.5">Signed</Badge>
                        ) : (
                          <Badge variant="secondary" className="text-[10px] px-1.5 py-0.5">Not Signed</Badge>
                        )}
                      </td>
                    )}
                    {/* Offer selected - Only show in RECEIVED stage */}
                    {stage === "RECEIVED" && (
                      <td className="px-3 py-2 whitespace-nowrap">
                        {app.prequal_offer_selected ? (
                          <Badge className="bg-green-600 text-[10px] px-1.5 py-0.5">Selected</Badge>
                        ) : (
                          <Badge variant="secondary" className="text-[10px] px-1.5 py-0.5">Not selected</Badge>
                        )}
                      </td>
                    )}
                    <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-500">{formatDateInEST(app.created_at)}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-500">
                      {app.est_close_date ? formatDateOnly(app.est_close_date) : '-'}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-500">{formatDateInEST(app.updated_at)}</td>
                    {showOrganizationColumn && (
                      <td className="px-3 py-2 text-xs text-gray-900 max-w-[11rem] truncate" title={app.organization_name || undefined}>
                        {app.organization_name || "—"}
                      </td>
                    )}
                    {stage === "FUNDED" && (
                      <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-500">
                        {app.funded_date ? formatDateInEST(app.funded_date) : '-'}
                      </td>
                    )}
                    <td className="px-3 py-2 whitespace-nowrap text-xs font-medium text-gray-900">{app.internal_id}</td>
                    <td className="px-3 py-2 text-xs text-gray-900">{app.equipment_name}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-900">${parseFloat(app.requested_amount || app.equipment_price || '0').toLocaleString()}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <Badge className={`${stageColors[app.stage] || "bg-gray-500"} text-[10px] px-1.5 py-0.5`}>
                        {stageLabels[app.stage] || app.stage}
                      </Badge>
                      {app.invite_url && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="ml-1 h-5 text-[10px] px-1.5"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigator.clipboard.writeText(app.invite_url!);
                          }}
                        >
                          Copy
                        </Button>
                      )}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-900">
                      {app.primary_contact ? `${app.primary_contact?.first_name || ""} ${app.primary_contact?.last_name || ""}`.trim() || "N/A" : "N/A"}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-900">{app.company?.name || "-"}</td>
                    <td className={`px-3 py-2 whitespace-nowrap text-xs text-gray-900 ${!showAllColumns ? 'xl:table-cell hidden' : ''}`}>
                      {app.assigned_sales_rep ? `${app.assigned_sales_rep?.first_name || ""} ${app.assigned_sales_rep?.last_name || ""}`.trim() : "-"}
                    </td>
                    <td className={`px-3 py-2 whitespace-nowrap ${!showAllColumns ? 'xl:table-cell hidden' : ''}`}>
                      {app.stips_requested && <Badge variant="secondary" className="text-[10px] px-1.5 py-0.5">Open</Badge>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Showing {data?.results.length || 0} of {data?.count || 0}
            </div>
            
            {/* Pagination Controls */}
            {data && data.count > 0 && (() => {
              const totalPages = Math.ceil(data.count / 50); // PAGE_SIZE is 50
              const currentPage = page;
              const pagesToShow: (number | string)[] = [];
              
              if (totalPages <= 7) {
                // Show all pages if 7 or fewer
                for (let i = 1; i <= totalPages; i++) {
                  pagesToShow.push(i);
                }
              } else {
                // Show first page, current page range, and last page
                pagesToShow.push(1);
                
                if (currentPage > 3) {
                  pagesToShow.push('...');
                }
                
                const start = Math.max(2, currentPage - 1);
                const end = Math.min(totalPages - 1, currentPage + 1);
                
                for (let i = start; i <= end; i++) {
                  pagesToShow.push(i);
                }
                
                if (currentPage < totalPages - 2) {
                  pagesToShow.push('...');
                }
                
                pagesToShow.push(totalPages);
              }
              
              return (
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (page > 1) {
                        const newPage = page - 1;
                        setPage(newPage);
                        setSearchParams(prev => {
                          const newParams = new URLSearchParams(prev);
                          newParams.set('page', newPage.toString());
                          return newParams;
                        });
                      }
                    }}
                    disabled={!data?.previous || page === 1}
                  >
                    Previous
                  </Button>
                  
                  {/* Page Numbers */}
                  <div className="flex items-center gap-1">
                    {pagesToShow.map((pageNum, idx) => (
                      pageNum === '...' ? (
                        <span key={`ellipsis-${idx}`} className="px-2 text-gray-500">...</span>
                      ) : (
                        <Button
                          key={pageNum}
                          variant={pageNum === currentPage ? "default" : "outline"}
                          size="sm"
                          className="min-w-[2rem]"
                          onClick={() => {
                            const newPage = pageNum as number;
                            setPage(newPage);
                            setSearchParams(prev => {
                              const newParams = new URLSearchParams(prev);
                              newParams.set('page', newPage.toString());
                              return newParams;
                            });
                          }}
                        >
                          {pageNum}
                        </Button>
                      )
                    ))}
                  </div>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (data?.next) {
                        const newPage = page + 1;
                        setPage(newPage);
                        setSearchParams(prev => {
                          const newParams = new URLSearchParams(prev);
                          newParams.set('page', newPage.toString());
                          return newParams;
                        });
                      }
                    }}
                    disabled={!data?.next}
                  >
                    Next
                  </Button>
                </div>
              );
            })()}
          </div>
        </>
      )}

      <Dialog open={manualIntakeOpen} onOpenChange={setManualIntakeOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Manual intake (paper / PDF)</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600">
            Creates a <strong>Received</strong> application and the same applicant link as Start Application. Use this when you are transcribing a paper deal or an existing signed PDF.
          </p>
          <div className="space-y-3 pt-2">
            <label className="flex items-start gap-2 text-sm text-gray-800 cursor-pointer">
              <input
                type="checkbox"
                className="mt-1"
                checked={manualHasSignedAuth}
                onChange={(e) => {
                  setManualHasSignedAuth(e.target.checked);
                  if (!e.target.checked) {
                    setManualSignedFile(null);
                    if (manualSignedFileInputRef.current) {
                      manualSignedFileInputRef.current.value = "";
                    }
                  }
                }}
              />
              <span>We already have the customer&apos;s signed authorization (upload PDF or image).</span>
            </label>
            {manualHasSignedAuth && (
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <Label htmlFor="manual-signed-file" className="text-gray-900">
                    Signed authorization
                  </Label>
                  {manualSignedFile ? (
                    <button
                      type="button"
                      className="text-xs font-medium text-blue-600 hover:underline"
                      onClick={() => {
                        setManualSignedFile(null);
                        if (manualSignedFileInputRef.current) {
                          manualSignedFileInputRef.current.value = "";
                        }
                      }}
                    >
                      Remove file
                    </button>
                  ) : null}
                </div>
                <input
                  ref={manualSignedFileInputRef}
                  id="manual-signed-file"
                  type="file"
                  accept=".pdf,image/*"
                  className="sr-only"
                  onChange={(e) => {
                    const f = e.target.files?.[0] ?? null;
                    if (f && !isAcceptableSignedAuthFile(f)) {
                      toast.error("Please choose a PDF or image (PNG, JPEG, WebP, or GIF).");
                      e.target.value = "";
                      setManualSignedFile(null);
                      return;
                    }
                    setManualSignedFile(f);
                  }}
                />
                <label
                  htmlFor="manual-signed-file"
                  onDragOver={(ev) => {
                    ev.preventDefault();
                    ev.stopPropagation();
                  }}
                  onDrop={(ev) => {
                    ev.preventDefault();
                    ev.stopPropagation();
                    const f = ev.dataTransfer.files?.[0];
                    if (!f) return;
                    if (!isAcceptableSignedAuthFile(f)) {
                      toast.error("Please drop a PDF or image (PNG, JPEG, WebP, or GIF).");
                      return;
                    }
                    setManualSignedFile(f);
                    if (manualSignedFileInputRef.current) {
                      manualSignedFileInputRef.current.value = "";
                    }
                  }}
                  className="mt-1 flex w-full cursor-pointer flex-col items-center gap-3 rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 p-5 text-center transition-colors hover:border-blue-400 hover:bg-blue-50/60 focus-within:ring-2 focus-within:ring-blue-500 focus-within:ring-offset-2"
                >
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 text-blue-700">
                    <Upload className="h-5 w-5" aria-hidden />
                  </span>
                  <span className="text-sm text-gray-700">
                    <span className="font-semibold text-blue-700">Choose file</span>
                    <span className="text-gray-600"> or drag and drop here</span>
                  </span>
                  <span className="inline-flex items-center rounded-md border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-900 shadow-sm">
                    Browse files
                  </span>
                  <span className="text-xs text-gray-500">PDF or image · up to 25 MB</span>
                  {manualSignedFile ? (
                    <span className="max-w-full truncate text-xs font-medium text-gray-800" title={manualSignedFile.name}>
                      Selected: {manualSignedFile.name}
                    </span>
                  ) : null}
                </label>
              </div>
            )}
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" type="button" onClick={() => setManualIntakeOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              className="bg-blue-600 text-white"
              disabled={createStaffManualIntake.isPending}
              onClick={() => createStaffManualIntake.mutate()}
            >
              {createStaffManualIntake.isPending ? "Creating…" : "Create link"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={jotformOpen}
        onOpenChange={(open) => {
          setJotformOpen(open);
          if (!open) {
            setJotformSuccess(null);
            setJotformReference("");
            importJotform.reset();
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Import Jotform</DialogTitle>
          </DialogHeader>
          {!jotformSuccess ? (
            <>
              <p className="text-sm text-gray-600">
                Paste the Jotform Submission ID or Ref from the HubSpot contact (TC-/MP- Unique ID
                or native submission ID). The form is detected automatically. The app is created
                under the selected organization.
              </p>
              <div className="space-y-3 pt-2">
                <div>
                  <Label htmlFor="jotform-reference">Jotform Submission ID or Ref</Label>
                  <Input
                    id="jotform-reference"
                    className="mt-1"
                    value={jotformReference}
                    onChange={(e) => setJotformReference(e.target.value)}
                    onPaste={(e) => {
                      const text = e.clipboardData.getData("text");
                      if (text != null) {
                        e.preventDefault();
                        setJotformReference(text.trim());
                      }
                    }}
                    placeholder="From HubSpot contact"
                    autoComplete="off"
                  />
                </div>
              </div>
              <DialogFooter className="gap-2 sm:gap-0">
                <Button variant="outline" type="button" onClick={() => setJotformOpen(false)}>
                  Cancel
                </Button>
                <Button
                  type="button"
                  className="bg-blue-600 text-white"
                  disabled={importJotform.isPending || !jotformReference.trim()}
                  onClick={() => importJotform.mutate()}
                >
                  {importJotform.isPending ? "Importing…" : "Import"}
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <p className="text-sm text-gray-600">
                {jotformSuccess.reused
                  ? "This submission was already imported. Review the existing apply link."
                  : "Application created. Review the prefilled form, then submit."}
                {!jotformSuccess.pdf_attached ? (
                  <span className="block mt-1 text-amber-700">
                    Signed PDF was not attached automatically.
                  </span>
                ) : null}
              </p>
              <div className="flex items-center gap-2 pt-2">
                <Input readOnly value={jotformSuccess.invite_url} className="flex-1" />
                <Button
                  variant="secondary"
                  type="button"
                  onClick={() => {
                    navigator.clipboard.writeText(jotformSuccess.invite_url);
                    toast.success("Link Copied");
                  }}
                >
                  Copy
                </Button>
              </div>
              <DialogFooter className="gap-2 sm:gap-0">
                <Button variant="outline" type="button" onClick={() => setJotformOpen(false)}>
                  Close
                </Button>
                <Button
                  type="button"
                  className="bg-blue-600 text-white"
                  onClick={() => {
                    setJotformOpen(false);
                    navigate(`/apply/${jotformSuccess.token}`);
                  }}
                >
                  Review application
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Application Link Created</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-gray-700">Share this link with the applicant:</div>
            <div className="flex items-center gap-2">
              <Input readOnly value={inviteUrl || ""} className="flex-1" />
              <Button
                variant="secondary"
                onClick={() => {
                  if (inviteUrl) {
                    navigator.clipboard.writeText(inviteUrl);
                    toast.success("Link Copied");
                  }
                }}
              >
                Copy
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                if (inviteUrl) {
                  window.open(inviteUrl, "_blank", "noopener,noreferrer");
                }
              }}
              disabled={!inviteUrl}
            >
              Open in new tab
            </Button>
            <Button onClick={() => setInviteOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={startAppModalOpen} onOpenChange={setStartAppModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>New Application</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600">Optionally add equipment before creating the link. The applicant can change it later.</p>
          <div className="space-y-3 pt-2">
            <div>
              <Label>Equipment Type</Label>
              <select
                value={startAppEquipment.equipment_type_id ?? ""}
                onChange={(e) => {
                  const val = e.target.value === "" ? null : Number(e.target.value);
                  const selected = val != null ? equipmentTypes.find((et: { id: number; subtypes?: { id: number; name: string }[] }) => et.id === val) : null;
                  const subtypes = selected?.subtypes ?? [];
                  const singleSubtypeId = subtypes.length === 1 ? subtypes[0].id : null;
                  setStartAppEquipment((prev) => ({
                    ...prev,
                    equipment_type_id: val,
                    equipment_subtype_id: val != null ? singleSubtypeId : null,
                  }));
                }}
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">None – get link without equipment</option>
                {allowedEquipmentTypes.map((et: { id: number; name: string; subtypes?: { id: number; name: string }[] }) => (
                  <option key={et.id} value={et.id}>{et.name}</option>
                ))}
              </select>
            </div>
            {startAppEquipment.equipment_type_id && (() => {
              const selected = equipmentTypes.find((et: { id: number; subtypes?: { id: number; name: string }[] }) => et.id === startAppEquipment.equipment_type_id);
              const subtypes = selected?.subtypes ?? [];
              if (subtypes.length <= 1) return null;
              return (
                <div>
                  <Label>Subtype</Label>
                  <select
                    value={startAppEquipment.equipment_subtype_id ?? ""}
                    onChange={(e) => {
                      const val = e.target.value === "" ? null : Number(e.target.value);
                      setStartAppEquipment((prev) => ({ ...prev, equipment_subtype_id: val }));
                    }}
                    className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">Optional</option>
                    {subtypes.map((sub: { id: number; name: string }) => (
                      <option key={sub.id} value={sub.id}>{sub.name}</option>
                    ))}
                  </select>
                </div>
              );
            })()}
            {startAppEquipment.equipment_type_id && (
              <>
                <div>
                  <Label>Description / Name</Label>
                  <Input
                    value={startAppEquipment.equipment_name}
                    onChange={(e) => setStartAppEquipment((prev) => ({ ...prev, equipment_name: e.target.value }))}
                    placeholder="e.g. 2022 John Deere 8R"
                    className="mt-1"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label>Year</Label>
                    <Input
                      type="number"
                      min={1900}
                      max={new Date().getFullYear() + 2}
                      placeholder="e.g. 2022"
                      value={startAppEquipment.year}
                      onChange={(e) => setStartAppEquipment((prev) => ({ ...prev, year: e.target.value }))}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Price ($)</Label>
                    <Input
                      type="number"
                      min={0}
                      placeholder="0"
                      value={startAppEquipment.price}
                      onChange={(e) => setStartAppEquipment((prev) => ({ ...prev, price: e.target.value }))}
                      className="mt-1"
                    />
                  </div>
                </div>
                {(() => {
                  const selected = equipmentTypes.find((et: { id: number; slug?: string; underwriting_signals?: string[] }) => et.id === startAppEquipment.equipment_type_id);
                  const signals = selected?.underwriting_signals ?? [];
                  const noMileageSlugs = ["waste-recycling-environmental-equipment", "sanitation-restroom-trailers"];
                  const showMileage = signals.includes("mileage") && !noMileageSlugs.includes(selected?.slug ?? "");
                  const showHours = signals.includes("hours");
                  if (!showMileage && !showHours) return null;
                  return (
                    <div className="grid grid-cols-2 gap-2">
                      {showMileage && (
                        <div>
                          <Label>Mileage</Label>
                          <Input
                            type="number"
                            min={0}
                            placeholder="e.g. 150000"
                            value={startAppEquipment.mileage}
                            onChange={(e) => setStartAppEquipment((prev) => ({ ...prev, mileage: e.target.value }))}
                            className="mt-1"
                          />
                        </div>
                      )}
                      {showHours && (
                        <div>
                          <Label>Hours</Label>
                          <Input
                            type="number"
                            min={0}
                            placeholder="e.g. 5000"
                            value={startAppEquipment.hours}
                            onChange={(e) => setStartAppEquipment((prev) => ({ ...prev, hours: e.target.value }))}
                            className="mt-1"
                          />
                        </div>
                      )}
                    </div>
                  );
                })()}
              </>
            )}
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              onClick={() => createInvite.mutate(startAppEquipment.equipment_type_id ? startAppEquipment as unknown as Record<string, unknown> : undefined)}
              disabled={createInvite.isPending}
            >
              {createInvite.isPending ? "Creating…" : "Create link"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
